import {
  ActionIcon,
  Autocomplete,
  Box,
  Button,
  Group,
  Loader,
  Text as MantineText,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { i18n } from '@lingui/core';
import { IconCheck, IconEdit, IconX } from '@tabler/icons';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { openDeleteConfirmModal } from '../../Atoms/DeleteConfirmModal';
import GrayBox from '../../Atoms/GrayBox';
import DocumentBubbleMenu from '../../Organismns/tiptap/toolbars/BubbleMenu';
import { RichInputWithoutMentions } from '../../Organismns/tiptap/TiptapEditorInput';
import { useEventScope } from '../../Service/hooks/useEventScope';
import { LogDto } from '../../Types/LogT';

interface Props {
  note: LogDto;
  updateHandler: (id: string, patch: Partial<LogDto>) => Promise<boolean>;
  deleteHandler: (id: string) => Promise<boolean>;
}

const descriptionSuggestions = ['Telefontermin', 'Onlinemeeting', 'Persönliches Treffen', 'Vor-Ort-Besprechung'];

const parseLogDateTime = (value?: Date | string | null) => {
  if (!value) return null;

  const parsedDateTime = new Date(value);
  return Number.isNaN(parsedDateTime.getTime()) ? null : parsedDateTime;
};

const formatDateTimeInput = (value?: Date | string | null) => {
  const parsedDateTime = parseLogDateTime(value);
  return parsedDateTime ? moment(parsedDateTime).format('YYYY-MM-DDTHH:mm') : '';
};

const NotePreviewItem: FC<Props> = ({ note, updateHandler, deleteHandler }) => {
  const { id, entityType, value, dateTime, description } = note;
  const { isEventScope } = useEventScope();
  const showBadge = isEventScope && note.entityType === 'contacts';
  const parsedDateTime = parseLogDateTime(dateTime);
  const [editingDetails, setEditingDetails] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsSaveFailed, setDetailsSaveFailed] = useState(false);
  const [editedDateTime, setEditedDateTime] = useState(() => formatDateTimeInput(dateTime));
  const [editedDescription, setEditedDescription] = useState(description ?? '');

  const resetDetails = () => {
    setEditedDateTime(formatDateTimeInput(dateTime));
    setEditedDescription(description ?? '');
    setDetailsSaveFailed(false);
  };

  useEffect(() => {
    setEditedDateTime(formatDateTimeInput(dateTime));
    setEditedDescription(description ?? '');
    setDetailsSaveFailed(false);
  }, [dateTime, description]);

  const cancelDetailsEdit = () => {
    resetDetails();
    setEditingDetails(false);
  };

  const saveDetails = async () => {
    setSavingDetails(true);
    setDetailsSaveFailed(false);

    const patch: Partial<LogDto> = {
      description: editedDescription,
    };
    if (editedDateTime) patch.dateTime = new Date(editedDateTime);

    try {
      const saved = await updateHandler(id, patch);
      setDetailsSaveFailed(!saved);
      if (saved) setEditingDetails(false);
    } catch {
      setDetailsSaveFailed(true);
    } finally {
      setSavingDetails(false);
    }
  };

  const delNote = () => {
    openDeleteConfirmModal({
      title: i18n._('event.notes.delete.confirm.head'),
      description: i18n._('event.notes.delete.confirm'),
      onConfirm: () => deleteHandler(id),
    });
  };

  return (
    <GrayBox p="xs">
      {!editingDetails && (
        <Box py={6} px={5} pos="relative">
          <Group position="center" spacing={6} noWrap px={30} sx={{ minWidth: 0 }}>
            <MantineText size="xs" weight="bolder" sx={{ whiteSpace: 'nowrap' }}>
              {parsedDateTime ? `${moment(parsedDateTime).format('DD.MM.YYYY, HH:mm')} Uhr` : 'Kein Datum'}
            </MantineText>
            <MantineText size="xs" color="dimmed">
              ·
            </MantineText>
            <MantineText
              size="xs"
              color="dimmed"
              sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {description || 'Keine Beschreibung'}
            </MantineText>
          </Group>
          <Tooltip label="Details bearbeiten" withArrow>
            <ActionIcon
              size="sm"
              variant="subtle"
              onClick={() => setEditingDetails(true)}
              sx={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)' }}
            >
              <IconEdit size={14} />
            </ActionIcon>
          </Tooltip>
        </Box>
      )}
      {editingDetails && (
        <Box py={6} px={5}>
          <Group spacing="xs" align="center">
            <TextInput
              type="datetime-local"
              aria-label="Datum und Uhrzeit"
              size="xs"
              value={editedDateTime}
              onChange={(event) => setEditedDateTime(event.currentTarget.value)}
              sx={{ width: 185 }}
            />
            <Autocomplete
              aria-label="Beschreibung"
              placeholder="Beschreibung"
              size="xs"
              data={descriptionSuggestions}
              value={editedDescription}
              onChange={setEditedDescription}
              sx={{ flex: 1, minWidth: 140 }}
            />
            <Group spacing={2} noWrap>
              <Tooltip label="Abbrechen" withArrow>
                <ActionIcon
                  aria-label="Abbrechen"
                  size="sm"
                  color="gray"
                  onClick={cancelDetailsEdit}
                  disabled={savingDetails}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Speichern" withArrow>
                <ActionIcon
                  aria-label="Speichern"
                  size="sm"
                  color="green"
                  variant="light"
                  onClick={saveDetails}
                  disabled={savingDetails}
                  sx={(theme) =>
                    theme.colorScheme === 'light'
                      ? {
                          color: theme.colors.green[8],
                          backgroundColor: theme.colors.green[1],
                          '&:hover': { backgroundColor: theme.colors.green[2] },
                        }
                      : {}
                  }
                >
                  {savingDetails ? <Loader size={12} /> : <IconCheck size={14} />}
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
          {detailsSaveFailed && (
            <MantineText mt={5} size="xs" color="red">
              Die Details konnten nicht gespeichert werden.
            </MantineText>
          )}
        </Box>
      )}
      <RichInputWithoutMentions
        includeListExtentions
        value={value}
        patchField={(html) => updateHandler(id, { value: html })}
        styles={{ minHeight: 100 }}
        menu={<DocumentBubbleMenu includeListOpts />}
      />
      <Group mt={5} position={showBadge ? 'center' : 'center'}>
        <Button size="xs" color="red" variant="subtle" onClick={delNote}>
          {`${entityType === 'events' ? 'Event' : 'Kontakt'}-Notiz löschen`}
        </Button>
      </Group>
    </GrayBox>
  );
};

export default NotePreviewItem;
